import { axiosClient } from "../api/axiosClient";

export type SpiritualScope = {
  terreiroId?: string;
  mediumId?: string;
};

export type SpiritualVisibility = "only_me" | "leaders" | "terreiro";

export type SpiritualDevelopmentStage =
  | "initial_contact"
  | "developing"
  | "confirmed"
  | "active_work";

export type SpiritualLine = {
  id: string;
  terreiroId?: string | null;
  name: string;
  side: "right" | "left" | "neutral";
  isSystemDefault: boolean;
  isActive: boolean;
};

export type SpiritualProfileItem = {
  id: string;
  type: "line" | "entity";
  lineId: string;
  line: string;
  side: "right" | "left" | "neutral";
  developmentStage: SpiritualDevelopmentStage | null;
  visibility: SpiritualVisibility;
  notes?: string | null;
  createdAt: string;
  entityId?: string;
  name?: string;
};

export type SpiritualEntityOption = {
  id: string;
  lineId: string;
  line: string;
  name: string;
  isCustom: boolean;
  side: "right" | "left" | "neutral";
  terreiroId?: string | null;
};

export type SpiritualProfileResponse = {
  profile: {
    id: string;
    mediumId: string;
    terreiroId: string;
    bio?: string | null;
    createdAt: string;
  };
  lines: SpiritualProfileItem[];
  entities: SpiritualProfileItem[];
};

export type SpiritualAnalyticsSummary = {
  totalMediums: number;
  totalEntities: number;
  topLines: Array<{ line: string; count: number }>;
};

export type AddSpiritualProfileEntityPayload = {
  lineId?: string;
  entityId?: string;
  entityName?: string;
  developmentStage: SpiritualDevelopmentStage;
  visibility: SpiritualVisibility;
  notes?: string;
} & SpiritualScope;

export type UpdateSpiritualProfileEntityPayload = {
  developmentStage?: SpiritualDevelopmentStage;
  visibility?: SpiritualVisibility;
  notes?: string | null;
};

export const spiritualProfileRepository = {
  async getMyProfile(scope: SpiritualScope): Promise<SpiritualProfileResponse> {
    const { data } = await axiosClient.get<SpiritualProfileResponse>(
      "/spiritual-profile/me",
      {
        params: scope,
      },
    );

    return data;
  },

  async getLines(scope: SpiritualScope): Promise<SpiritualLine[]> {
    const { data } = await axiosClient.get<SpiritualLine[]>("/spiritual-lines", {
      params: scope,
    });

    return data;
  },

  async getEntities(
    scope: SpiritualScope,
    lineId?: string,
  ): Promise<SpiritualEntityOption[]> {
    const { data } = await axiosClient.get<SpiritualEntityOption[]>(
      "/spiritual-entities",
      {
        params: {
          ...scope,
          ...(lineId ? { lineId } : {}),
        },
      },
    );

    return data;
  },

  async addEntity(
    payload: AddSpiritualProfileEntityPayload,
  ): Promise<SpiritualProfileItem> {
    const { data } = await axiosClient.post<SpiritualProfileItem>(
      "/spiritual-profile/entities",
      payload,
    );

    return data;
  },

  async updateEntity(
    id: string,
    payload: UpdateSpiritualProfileEntityPayload,
    scope: SpiritualScope,
  ): Promise<SpiritualProfileItem> {
    const { data } = await axiosClient.patch<SpiritualProfileItem>(
      `/spiritual-profile/entities/${id}`,
      payload,
      { params: scope },
    );

    return data;
  },

  async removeEntity(id: string, scope: SpiritualScope): Promise<void> {
    await axiosClient.delete(`/spiritual-profile/entities/${id}`, {
      params: scope,
    });
  },

  async getAnalyticsSummary(
    terreiroId: string,
  ): Promise<SpiritualAnalyticsSummary> {
    const { data } = await axiosClient.get<SpiritualAnalyticsSummary>(
      "/spiritual-analytics/summary",
      {
        params: { terreiroId },
      },
    );

    return data;
  },
};
